// Notification templates store variables as plain "{{var}}" tokens — that is what the backend
// substitutes when the notification is sent (see config/notification.php and NotificationService).
// The editors show those tokens as inline chips instead, so both directions of that conversion live
// here: token text -> chips (on load) and chips -> token text (on change).
//
// Only the canonical "{{id}}" form is converted. Anything else (e.g. "{{ id }}") is left as literal
// text, so opening and saving a template that we do not recognise cannot alter its stored value.

// A checkout addon variable is its exact name: {{addon:<name>}}.
export const VARIABLE_TOKEN_PATTERN = /\{\{([A-Za-z0-9_-]+|addon:[^{}\r\n]+)\}\}/gu;
export const VARIABLE_CHIP_CLASS = "bo-variable-chip";
export const VARIABLE_CHIP_ATTR = "data-variable";

// The "/" trigger for the variable suggest list. The "/" has to open a token - it is either at the
// start of the line or preceded by whitespace - so URLs and "and/or" do not fire it. A chip's caret
// anchor (U+200B) and the embed character Quill uses for a chip (U+FFFC) both count as a boundary,
// since the caret sits right after them. The query cannot contain a space or another "/".
export const VARIABLE_TRIGGER_PATTERN = /(?:^|[\s\u200B\uFFFC])\/([^\s/]*)$/;

// Given the text immediately before the caret, return the "/query" being typed - { query, start },
// where `start` is the index of the "/" - or null when the caret is not inside such a token.
export function matchVariableTrigger(textBeforeCaret = "") {
    const match = VARIABLE_TRIGGER_PATTERN.exec(textBeforeCaret);

    if (!match) {
        return null;
    }

    const query = match[1];

    return { query, start: textBeforeCaret.length - query.length - 1 };
}

// id -> the variable's own definition, so the sample travels with the label
function variableMap(variables = []) {
    const map = new Map();

    (variables ?? []).forEach((variable) => {
        if (variable?.id) {
            map.set(variable.id, {
                id: variable.id,
                label: variable.label ?? variable.id,
                sample: variable.sample,
            });
        }
    });

    return map;
}

// "Preview with sample data" shows a sample from the variable's own definition (config/notification.php),
// so a variable is added in exactly one place: its `sample` rides along with its `id` and `label`.
export const GENERIC_SAMPLE = "Sample value";

// The two map-shaped definitions (document_variables, promotion_variables) are `id => label`, so they
// have nowhere to hang a sample. Rather than keep a parallel list in step, read one off the id.
const SAMPLE_RULES = [
    [/(^|_)date$|_at$/, "12/10/2026"],
    [/_code$/, "C0001"],
    [/(^|_)number$/, "SO-000123"],
    [/_count$|_total$|outstanding$|_amount$/, "1,234.00"],
    [/_list$/, "Item 1, Item 2, Item 3"],
    [/_link$|_url$/, "https://example.com/link"],
    [/_status$/, "Approved"],
    [/(company|site)_name$|^site_title$/, "Acme Sdn Bhd"],
    [/_email$|^email$/, "jane@acme.com"],
    [/(created|updated|approved|submitter|approver|_by)$/, "Jane Tan"],
    [/(^|_)remark$|_content$|_note$/, "Please review the attached."],
    [/_id$/, "12345"],
];

export function sampleValueFromVariable(variable) {
    if (variable?.sample) {
        return variable.sample;
    }

    const id = variable?.id ?? "";

    for (const [pattern, sample] of SAMPLE_RULES) {
        if (pattern.test(id)) {
            return sample;
        }
    }

    return GENERIC_SAMPLE;
}

// The chip's visible text is the human label; the token it stands for lives in data-variable. In
// preview mode it shows a sample value instead, flagged so it can be styled differently.
export function createChipNode(variable, preview = false) {
    const chip = document.createElement("span");
    chip.className = preview ? `${VARIABLE_CHIP_CLASS} preview` : VARIABLE_CHIP_CLASS;
    chip.setAttribute(VARIABLE_CHIP_ATTR, variable.id);
    chip.setAttribute("contenteditable", "false");
    chip.textContent = preview ? sampleValueFromVariable(variable) : variable.label ?? variable.id;

    return chip;
}

function chipHtml(variable, preview = false) {
    const chip = createChipNode(variable, preview);

    return chip.outerHTML;
}

// Tokens -> chips, for a stored HTML string (the rich-text fields). Only text nodes are touched, so a
// token that happens to sit inside an attribute is left alone.
export function tokenTextToChipHtml(html, variables = [], preview = false) {
    if (!html) {
        return html ?? "";
    }

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    const variablesById = variableMap(variables);
    const textNodes = [];

    const collectTextNodes = (node) => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                textNodes.push(child);
            } else if (child.nodeType === Node.ELEMENT_NODE && !child.hasAttribute(VARIABLE_CHIP_ATTR)) {
                collectTextNodes(child);
            }
        });
    };
    collectTextNodes(doc.body);

    textNodes.forEach((textNode) => {
        const text = textNode.nodeValue;
        if (!text?.includes("{{")) {
            return;
        }

        const fragment = doc.createDocumentFragment();
        let lastIndex = 0;

        text.replace(VARIABLE_TOKEN_PATTERN, (match, id, offset) => {
            if (offset > lastIndex) {
                fragment.appendChild(doc.createTextNode(text.slice(lastIndex, offset)));
            }
            fragment.appendChild(createChipNode(variablesById.get(id) ?? { id }, preview));
            lastIndex = offset + match.length;

            return match;
        });

        if (lastIndex === 0) {
            return;
        }

        if (lastIndex < text.length) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
        }
        textNode.replaceWith(fragment);
    });

    return doc.body.innerHTML;
}

// Chips -> tokens, for a stored HTML string (the rich-text fields).
export function chipHtmlToTokenText(html) {
    if (!html) {
        return html ?? "";
    }

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");

    doc.body.querySelectorAll(`[${VARIABLE_CHIP_ATTR}]`).forEach((chip) => {
        chip.replaceWith(doc.createTextNode(`{{${chip.getAttribute(VARIABLE_CHIP_ATTR)}}}`));
    });

    return doc.body.innerHTML;
}

function appendTextWithBreaks(fragment, text, doc = document) {
    const lines = String(text).split("\n");

    lines.forEach((line, index) => {
        if (index > 0) {
            fragment.appendChild(doc.createElement("br"));
        }
        if (line !== "") {
            fragment.appendChild(doc.createTextNode(line));
        }
    });
}

// Tokens -> chips as DOM nodes, for the plain-text fields' contenteditable surface.
export function tokenTextToFragment(text, variables = [], preview = false) {
    const value = String(text ?? "");
    const fragment = document.createDocumentFragment();
    const variablesById = variableMap(variables);
    let lastIndex = 0;

    value.replace(VARIABLE_TOKEN_PATTERN, (match, id, offset) => {
        if (offset > lastIndex) {
            appendTextWithBreaks(fragment, value.slice(lastIndex, offset));
        }
        fragment.appendChild(createChipNode(variablesById.get(id) ?? { id }, preview));
        // every chip gets a caret anchor after it, the same as a freshly inserted one - without it a
        // reopened notification loses the caret position that sits immediately after the chip
        fragment.appendChild(document.createTextNode(CARET_ANCHOR));
        lastIndex = offset + match.length;

        return match;
    });

    if (lastIndex < value.length) {
        appendTextWithBreaks(fragment, value.slice(lastIndex));
    }

    return fragment;
}

// The contenteditable surface -> tokens. Chips become "{{id}}"; block breaks and <br> become "\n".
export function elementToTokenText(element) {
    if (!element) {
        return "";
    }

    let text = "";

    const walk = (node) => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                // contenteditable leans on non-breaking spaces for trailing/adjacent whitespace, and a
                // zero-width space is never part of the value
                text += child.nodeValue.replace(/\u00A0/g, " ").replace(/\u200B/g, "");
                return;
            }

            if (child.nodeType !== Node.ELEMENT_NODE) {
                return;
            }

            if (child.tagName === "BR") {
                text += "\n";
                return;
            }

            if (child.hasAttribute(VARIABLE_CHIP_ATTR)) {
                text += `{{${child.getAttribute(VARIABLE_CHIP_ATTR)}}}`;
                return;
            }

            if ((child.tagName === "DIV" || child.tagName === "P") && text !== "" && !text.endsWith("\n")) {
                text += "\n";
            }

            walk(child);
        });
    };

    walk(element);

    // a lone <br> (or a run of empty blocks) is how browsers leave an emptied contenteditable -
    // it is not a line the user typed
    return /^[\n]*$/.test(text) && !element.querySelector(`[${VARIABLE_CHIP_ATTR}]`) ? "" : text;
}

export const CARET_ANCHOR = "\u200B";

export function isCaretAnchor(node) {
    return node?.nodeType === Node.TEXT_NODE && node.nodeValue === CARET_ANCHOR;
}

// A chip is atomic, so with nothing editable after it the browser has no text position for the caret
// and falls back to a child-index one that it draws in the wrong place. A zero-width text node gives
// the caret somewhere real to sit. Stripped from the value when the field is serialized.
export function ensureTrailingCaretAnchor(editor) {
    const last = editor?.lastChild;

    if (last?.nodeType === Node.ELEMENT_NODE && last.hasAttribute(VARIABLE_CHIP_ATTR)) {
        editor.appendChild(document.createTextNode(CARET_ANCHOR));
    }
}
